import React, { useState, useRef, useEffect } from "react";
import {
    errorMessage,
    formatDate,
    formatStatus,
    formatUsername,
} from "../../../util/Helpers";
import cogoToast from "cogo-toast";
import { Permissions, PermissionType } from "../../../util/Permissions";
import DynamicSimpleTable from "../../../util/DynamicSimpleReactTable";
import { Button } from "react-bootstrap";
import { Permission } from "../../Common/layout/Permission";
import { EditIcon, RefreshIcon, TrashIcon, ViewIcon } from "../../../util/Icons";
import ViewSubscriptionModal from "./modals/ViewSubscriptionModal";
import CancelSubscriptionModal from "./modals/CancelSubscriptionModal";
import RenewSubscriptionModal from "./modals/RenewSubscriptionModal";
import EditSubscriptionModal from "./modals/EditSubscriptionModal";
import { Card, CardBody, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import { FilterGroup } from "../../Common/components/FilterGroup";
import { useDownloadSubscriptionReportMutation } from "../../../../redux/apiSlice";

export const SubscriptionTable = ({ userGroup, userId, accounts = [] }) => {
    const tableRef = useRef();

    const [
        downloadSubscriptionReport,
        {
            isLoading: isLoadingSubscriptionReport,
            isSuccess: isSubscriptionReportGenerating,
        }
    ] = useDownloadSubscriptionReportMutation()

    useEffect(() => {
        if (isSubscriptionReportGenerating) {
            cogoToast.success(`Subscription export in progress. The resulting file will be sent to your mail.`);
        }
    }, [isSubscriptionReportGenerating])

    const [subscriptionModal, setSubscriptionModal] = useState({
        showViewSubscriptionModal: false,
        showCreateSubscriptionModal: false,
        showEditSubscriptionModal: false,
        showRenewSubscriptionModal: false,
        showCancelSubscriptionModal: false,
        details: null
    });

    /* Modal show/hide Toggle */
    const toggleModal = modal => _ => {
        setSubscriptionModal({ ...subscriptionModal, [modal]: !subscriptionModal[modal] })
    }

    const viewSubscription = details => {
        setSubscriptionModal({ ...subscriptionModal, showViewSubscriptionModal: true, details });
    }

    /* Create Subscription Modal */
    const createSubscription = _ => {
        setSubscriptionModal({
            ...subscriptionModal,
            showCreateSubscriptionModal: true,
            details: { userId }
        })
    }

    /* Edit Subscription Modal */
    const editSubscription = details => {
        setSubscriptionModal({
            ...subscriptionModal,
            showEditSubscriptionModal: true,
            details
        })
    }

    /* Renew Subscription Modal */
    const renewSubscription = details => {
        setSubscriptionModal({
            ...subscriptionModal,
            showRenewSubscriptionModal: true,
            details
        })
    }

    /* Cancel Subscription Modal */
    const cancelSubscription = details => {
        setSubscriptionModal({
            ...subscriptionModal,
            showCancelSubscriptionModal: true,
            details
        })
    }

    /* Action Buttons */
    const subscriptionAction = (row) => {
        return (
            <span style={{ whiteSpace: "nowrap" }}>
                <Permission permission={Permissions.SUBSCRIPTIONS} permissionType={PermissionType.READ}>
                    <Button
                        title="View Subscription"
                        className="btn btn-icon btn-light btn-hover-primary btn-sm bg-white"
                        onClick={_ => viewSubscription(row)}
                    >
                        <span className="svg-icon svg-icon-md svg-icon-primary">
                            <ViewIcon />
                        </span>
                    </Button>
                </Permission>

                <Permission permission={Permissions.SUBSCRIPTIONS} permissionType={PermissionType.UPDATE}>
                    <Button
                        title="Edit Subscription"
                        className={`btn btn-icon btn-light btn-hover-primary btn-sm ml-3`}
                        onClick={_ => editSubscription(row)}
                    >
                        <span className="svg-icon svg-icon-md svg-icon-primary">
                            <EditIcon />
                        </span>
                    </Button>
                </Permission>

                <Permission permission={Permissions.SUBSCRIPTIONS} permissionType={PermissionType.UPDATE}>
                    <Button
                        title="Renew Subscription"
                        className={`btn btn-icon btn-light btn-hover-primary btn-sm ml-3`}
                        onClick={_ => renewSubscription(row)}
                    >
                        <span className="svg-icon svg-icon-md svg-icon-primary">
                            <RefreshIcon />
                        </span>
                    </Button>
                </Permission>

                <Permission permission={Permissions.SUBSCRIPTIONS} permissionType={PermissionType.DELETE}>
                    <Button
                        title="Cancel Subscription"
                        className={`btn btn-icon btn-light btn-hover-danger btn-sm ml-3`}
                        onClick={_ => cancelSubscription(row)}
                    >
                        <span className="svg-icon svg-icon-md svg-icon-primary">
                            <TrashIcon />
                        </span>
                    </Button>
                </Permission>
            </span>
        );
    };

    return (
        <>
            <FilterGroup key={"subscriptions"}>
                <div className="d-flex mb-4">
                    <FilterGroup.Filter name="product" label="Type">
                        <FilterGroup.Option value="">All</FilterGroup.Option>
                        <FilterGroup.Option value="social-power-user">Social Power User</FilterGroup.Option>
                        <FilterGroup.Option value="market-insight-ngn">Market Insight</FilterGroup.Option>
                    </FilterGroup.Filter>

                    <div className="ml-3">
                        <FilterGroup.Filter name="status" label="Status">
                            <FilterGroup.Option value="">All</FilterGroup.Option>
                            <FilterGroup.Option value="ACTIVE">Active</FilterGroup.Option>
                            <FilterGroup.Option value="INACTIVE">Inactive</FilterGroup.Option>
                        </FilterGroup.Filter>
                    </div>
                </div>


                <FilterGroup.Content className="mt-5">
                    {({ filters }) => (
                        <Card>
                            <CardHeader title={`Subscriptions`}>
                            <Permission permission={Permissions.REPORTS} permissionType={PermissionType.CREATE}>
                                                <CardHeaderToolbar>
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        disabled={isLoadingSubscriptionReport}
                                                        onClick={() => downloadSubscriptionReport({
                                                            userGroup,
                                                            startDate: filters?.startDate || "",
                                                            endDate: filters?.endDate || "",
                                                            status: filters?.status || "",
                                                            type: filters?.type || "",
                                                            format: "csv",
                                                        })}
                                                    >
                                                        Export CSV
                                                    </button>
                                                </CardHeaderToolbar>
                                            </Permission>
                                {userId && (
                                    <Permission
                                        permission={Permissions.SUBSCRIPTIONS}
                                        permissionType={PermissionType.CREATE}
                                    >
                                        <CardHeaderToolbar>
                                            <Button onClick={createSubscription}>Create Subscription</Button>
                                        </CardHeaderToolbar>
                                    </Permission>
                                )}
                            </CardHeader>

                            <CardBody>
                                <DynamicSimpleTable
                                    key={`${userGroup}-subscriptions`}
                                    queryParams={
                                        `userGroup=${userGroup}`
                                        + (userId ? `&userId=${userId}` : "")
                                        + `&frequency=${filters?.frequency || ""}`
                                        + `&product=${filters?.product || ""}`
                                        + `&status=${filters?.status || ""}`
                                    }
                                    searchBox={true}
                                    source={{ url: `/subscriptions` }}
                                    columns={[
                                        { title: "PRODUCT", key: "product" },
                                        { title: "SUB KEY", key: "subKey" },
                                        { title: "FREQUENCY", key: "frequency" },
                                        { title: "AUTO RENEW", key: "autoRenew" },
                                        ...(!userId
                                            ? [{
                                                title: "USER",
                                                format: row => formatUsername(row.userId, row.username, null, null, "subscriptions"),
                                            }]
                                            : []),
                                        { title: "DUE DATE", format: row => formatDate(row.dueDate) },
                                        { title: "STATUS", format: row => formatStatus(row.status) },
                                        { title: "CREATED AT", format: row => formatDate(row.created) },
                                        { title: "UPDATED AT", format: row => formatDate(row.updated) },
                                        { title: "", format: subscriptionAction },
                                    ]}
                                    ref={tableRef}
                                    onError={err => cogoToast.error(errorMessage(err))}
                                    refreshIcon={true}
                                />
                            </CardBody>
                        </Card>
                    )}
                </FilterGroup.Content>
            </FilterGroup>


            <ViewSubscriptionModal
                {...subscriptionModal}
                onHide={toggleModal("showViewSubscriptionModal")}
            />

            {/* Create Subscription Modal */}
            <EditSubscriptionModal
                showEditSubscriptionModal={subscriptionModal.showCreateSubscriptionModal}
                details={subscriptionModal.details}
                tableRef={tableRef}
                isNew={true}
                accountOptions={accounts}
                onHide={toggleModal("showCreateSubscriptionModal")}
            />

            {/* Update Subscription Modal */}
            <EditSubscriptionModal
                {...subscriptionModal}
                tableRef={tableRef}
                onHide={toggleModal("showEditSubscriptionModal")}
            />

            <RenewSubscriptionModal
                {...subscriptionModal}
                tableRef={tableRef}
                onHide={toggleModal("showRenewSubscriptionModal")}
            />

            <CancelSubscriptionModal
                {...subscriptionModal}
                tableRef={tableRef}
                onHide={toggleModal("showCancelSubscriptionModal")}
            />
        </>
    );
};
